/**
 * Microsoft Teams meeting and call records -- one row per meeting or call, at the time it
 * happened.
 *
 * Origin: IR-20261001-ahayman-001 (operator, 2026-10-01). The case timeline showed 32 rows
 * of "performed MeetingParticipantDetail" (and 12 of CallParticipantDetail) with private
 * addresses, several to a second, and the operator could not tell whether any of it was the
 * attacker.
 *
 * What the records actually are (measured on that case's run blob):
 *
 *   - Microsoft writes ONE `MeetingDetail` per meeting and ONE `MeetingParticipantDetail` PER
 *     ATTENDEE into the ORGANIZER's audit log; a call writes ONE `CallParticipantDetail` PER
 *     PARTICIPANT, keyed by `CallId`. So 20 of the case's 26 meeting-participant rows and 6 of
 *     its 12 call rows were other people's participation, recorded under the case user's name.
 *     The participant is in `RawLog.Attendees[0]`; that, not `UserId`, says whose a row is.
 *   - `ClientIP` is what the Teams app reports: usually the device's LOCAL network address
 *     (192.168.x / 10.x), sometimes a masked public one (`96.8.251.x`). It identifies no one.
 *   - The record's time is when Microsoft WROTE it, typically hours after the meeting ended
 *     (a 13:58-14:33Z meeting was written at 22:57:41Z). The real times are `StartTime`/
 *     `EndTime` and each participant's `JoinTime`/`LeaveTime`, all UTC with no zone suffix.
 *     Left uncorrected, a morning meeting lands inside an afternoon attacker window.
 *
 * So this module does two things, and keeps them apart on purpose:
 *
 *   1. FACTS (automatic): where results load, each record is re-timed to the activity time
 *      and stamped with the ids of every record of its meeting/call (`normalizeTeamsRecords`).
 *      In the shared roll-up (`applyAllEventsRollups`, so the screen and the reports group
 *      identically) the records of one meeting/call become ONE row (`buildTeamsMeetingsInStream`).
 *      Nothing is discarded: every participant stays in the expanded row, and the row's
 *      `_collapsedIds` is the WHOLE meeting -- including records a filter or an earlier
 *      dismissal hid -- so "hide this meeting" hides all of it and "restore" restores all of it.
 *
 *   2. EVIDENCE for the analyst's decision (never automatic): whether the case user's OWN
 *      participation was really them. Device name, Teams app and address are reported by the
 *      app on the device, so no single field proves anything; consistency with participation
 *      BEFORE the attacker's first recorded activity is the evidence (`buildMeetingDeviceBaseline`
 *      / `assessAttendance`), alongside the user's sign-ins in the same minutes (`nearbySignIns`).
 *      Hiding is always the analyst's act -- an attacker joining or scheduling meetings from the
 *      account is a real tactic, and their join from their own machine shows as a device that
 *      was never seen before.
 *
 * Pure module: no React, no imports.
 */

export const TEAMS_ATTENDANCE_OPS = new Set(['MeetingParticipantDetail', 'CallParticipantDetail']);
export const TEAMS_RECORD_OPS = new Set(['MeetingDetail', ...TEAMS_ATTENDANCE_OPS]);

/** Minutes either side of a join time searched for the user's own sign-ins. */
export const MEETING_SIGNIN_WINDOW_MIN = 5;

function rawOf(e) {
  const r = e?.RawLog;
  if (!r) return null;
  if (typeof r === 'string') {
    try { return JSON.parse(r); } catch { return null; }
  }
  return r;
}
function opOf(e) { return e?.Operation || rawOf(e)?.Operation || ''; }

/** A UTC time with no zone suffix (as Teams writes them) to ISO with `Z`; null if unparseable. */
export function teamsUtc(value) {
  if (!value || typeof value !== 'string') return null;
  const s = /[zZ]$|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export function isTeamsMeetingRecord(e) {
  if (!e || !TEAMS_RECORD_OPS.has(opOf(e))) return false;
  const wl = e.Workload || rawOf(e)?.Workload;
  return !wl || String(wl).toLowerCase() === 'microsoftteams';
}

/** The meeting/call a record belongs to: `m:<meeting id>` or `c:<call id>`; '' when unknown. */
export function teamsGroupKey(e) {
  if (!isTeamsMeetingRecord(e)) return '';
  const r = rawOf(e);
  if (!r) return '';
  const op = opOf(e);
  if (op === 'CallParticipantDetail') return r.CallId ? `c:${r.CallId}` : '';
  if (op === 'MeetingParticipantDetail') return r.MeetingDetailId ? `m:${r.MeetingDetailId}` : '';
  return r.Id ? `m:${r.Id}` : '';
}

/** When the activity happened: a participant's join time, or the meeting's start. */
export function meetingActivityTime(e) {
  const r = rawOf(e);
  if (!r) return null;
  return TEAMS_ATTENDANCE_OPS.has(opOf(e)) ? teamsUtc(r.JoinTime) : teamsUtc(r.StartTime);
}

/**
 * Re-time a Teams meeting/call record to when the activity happened. The time Microsoft wrote
 * the record is kept as `RecordedTimeUTC`. Any other record, or one with no parseable activity
 * time, is returned unchanged.
 */
export function correctMeetingRecordTime(e) {
  if (!isTeamsMeetingRecord(e)) return e;
  if (e.RecordedTimeUTC) return e; // already corrected
  const t = meetingActivityTime(e);
  if (!t) return e;
  return { ...e, RecordedTimeUTC: e.EventTimeUTC || '', EventTimeUTC: t };
}

/**
 * Applied ONCE where results load (`normalizeResults`): re-time every Teams record and stamp
 * each with `_meetingRecordIds`, the ids of EVERY record of its meeting/call. The roll-up only
 * sees what survived the filters and dismissals, so without the stamp a meeting whose own
 * attendance had been dismissed would describe itself as having none.
 */
export function normalizeTeamsRecords(events) {
  const timed = (events || []).map(correctMeetingRecordTime);
  const ids = new Map();
  for (const e of timed) {
    const k = teamsGroupKey(e);
    if (!k || !e.EventId) continue;
    if (!ids.has(k)) ids.set(k, []);
    ids.get(k).push(e.EventId);
  }
  if (ids.size === 0) return timed;
  return timed.map((e) => {
    const k = teamsGroupKey(e);
    return k && ids.has(k) ? { ...e, _meetingRecordIds: [...ids.get(k)].sort() } : e;
  });
}

/**
 * The Teams app from the record's user agent. `CallSignalingAgent (49/…` is the desktop app,
 * 1416 Android and 1417 iOS (each confirmed against the device models on the reference case:
 * Latitude laptops, Samsung/Pixel phones, iPhones). A Microsoft service (e.g. voicemail) is
 * named by its own agent; anything else by its code.
 */
export function teamsClientOf(userAgent) {
  const ua = String(userAgent || '');
  if (/Voicemail/i.test(ua)) return { code: 'voicemail', label: 'Teams voicemail service' };
  const m = /CallSignalingAgent \((\d+)\//.exec(ua);
  if (!m) return { code: '', label: ua ? 'Teams client' : '' };
  const code = m[1];
  const label = { 49: 'Teams desktop', 1416: 'Teams Android', 1417: 'Teams iOS' }[code] || `Teams client ${code}`;
  return { code, label };
}

function userAgentOf(raw) {
  const p = (raw?.ExtraProperties || []).find((x) => x && x.Key === 'UserAgent');
  return p ? p.Value : '';
}

/**
 * One participant's participation, from a MeetingParticipantDetail or CallParticipantDetail
 * record. `isSubject` is true when the participant IS the account whose audit log carries the
 * record (`UserKey` is that account's object id) -- self-contained, so no identity list has to
 * be threaded in. Call records carry no UPN field; some carry the UPN as the display name.
 */
export function attendanceOf(e) {
  const r = rawOf(e);
  if (!r || !TEAMS_ATTENDANCE_OPS.has(opOf(e))) return null;
  const a = (r.Attendees || [])[0] || {};
  const owner = String(r.UserKey || '').toLowerCase();
  const ownerUpn = String(r.UserId || e.UserPrincipalName || '').toLowerCase();
  const upn = a.UPN || (String(a.DisplayName || '').includes('@') ? a.DisplayName : '');
  const isSubject = (!!owner && String(a.UserObjectId || '').toLowerCase() === owner)
    || (!!ownerUpn && String(upn).toLowerCase() === ownerUpn);
  const client = teamsClientOf(userAgentOf(r));
  return {
    eventId: e.EventId,
    upn,
    name: a.DisplayName || '',
    objectId: a.UserObjectId || '',
    recipientType: a.RecipientType || '',
    isGuest: /guest/i.test(a.RecipientType || '') || a.IsAADGuest === true,
    isOrganizer: a.IsOrganizer === true,
    isSubject,
    localIp: r.ClientIP || e.IPAddress || '',
    device: r.DeviceInformation || '',
    clientCode: client.code,
    clientLabel: client.label,
    join: teamsUtc(r.JoinTime),
    leave: teamsUtc(r.LeaveTime),
  };
}

function minutesBetween(a, b) {
  const x = Date.parse(a); const y = Date.parse(b);
  if (Number.isNaN(x) || Number.isNaN(y) || y < x) return null;
  return Math.round((y - x) / 60000);
}

function durationLabel(min) {
  if (min == null) return '';
  if (min < 60) return `${Math.max(1, min)} min`;
  const h = Math.floor(min / 60); const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * Roll every Teams meeting/call record in a mixed stream into ONE row per meeting or call,
 * placed where its first record was. Records of other types pass through untouched.
 */
export function buildTeamsMeetingsInStream(stream) {
  const groups = new Map();
  for (const e of stream || []) {
    const k = teamsGroupKey(e);
    if (!k) continue;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(e);
  }
  if (groups.size === 0) return stream;

  const emitted = new Set();
  const out = [];
  for (const e of stream) {
    const k = teamsGroupKey(e);
    if (!k || !groups.has(k)) { out.push(e); continue; }
    if (emitted.has(k)) continue;
    emitted.add(k);
    out.push(buildMeetingRow(k, groups.get(k)));
  }
  return out;
}

function buildMeetingRow(key, members) {
  const isCall = key.startsWith('c:');
  const detail = members.find((m) => opOf(m) === 'MeetingDetail') || null;
  const dr = detail ? rawOf(detail) : null;
  const attendees = members.map(attendanceOf).filter(Boolean)
    .sort((a, b) => (b.isSubject - a.isSubject) || String(a.join || '').localeCompare(String(b.join || '')));

  const times = members.map((m) => m.EventTimeUTC).filter(Boolean).sort();
  const joins = attendees.map((a) => a.join).filter(Boolean).sort();
  const leaves = attendees.map((a) => a.leave).filter(Boolean).sort();
  const start = teamsUtc(dr?.StartTime) || joins[0] || times[0] || '';
  const end = teamsUtc(dr?.EndTime) || leaves[leaves.length - 1] || '';

  const anyRaw = rawOf(members[0]) || {};
  const owner = String(anyRaw.UserKey || '').toLowerCase();
  const organizerObjectId = isCall ? '' : ((dr?.Organizer || anyRaw.Organizer || {}).UserObjectId || '');
  const subject = attendees.filter((a) => a.isSubject);
  const primary = detail || members[0];

  // The WHOLE meeting's record ids, including any a filter or a dismissal removed from this
  // stream (stamped at load by normalizeTeamsRecords; the members alone otherwise).
  const present = members.map((m) => m.EventId);
  const full = [...new Set([...present, ...members.flatMap((m) => m._meetingRecordIds || [])])].sort();

  return {
    ...primary,
    EventId: primary.EventId,
    EventTimeUTC: start || primary.EventTimeUTC,
    RecordedTimeUTC: primary.RecordedTimeUTC || '',
    Operation: isCall ? 'TeamsCall' : 'TeamsMeeting',
    // The Client IP column shows the case user's OWN reported address when they took part, and
    // nothing otherwise -- never another participant's address under this account's name.
    IPAddress: subject[0]?.localIp || '',
    _meeting: {
      type: isCall ? 'call' : 'meeting',
      meetingId: key,
      start,
      end,
      durationMin: minutesBetween(start, end),
      organizerObjectId,
      organizerIsSubject: isCall ? false : (!!owner && organizerObjectId.toLowerCase() === owner),
      outcome: isCall ? (anyRaw.ItemName || '') : '',
      kind: isCall ? '' : (dr?.CommunicationSubType || dr?.ItemName || anyRaw.ItemName || ''),
      modalities: dr?.Modalities || '',
      attendees,
      subjectAttendances: subject,
      otherAttendeeCount: new Set(attendees.filter((a) => !a.isSubject).map((a) => (a.upn || a.name || a.objectId).toLowerCase())).size,
      hiddenRecordCount: full.length - present.length,
    },
    _meetingRowCount: members.length,
    _collapsedIds: full,
  };
}

function withWhom(m) {
  const names = m.attendees.filter((a) => !a.isSubject).map((a) => a.name || a.upn || 'unknown');
  const uniq = [...new Set(names)];
  if (!uniq.length) return '';
  return uniq.length <= 2 ? uniq.join(' and ') : `${uniq[0]} and ${uniq.length - 1} others`;
}

function ownPart(m, noun) {
  if (m.subjectAttendances.length) {
    return m.subjectAttendances.map((a) => {
      const d = m.type === 'call' ? '' : durationLabel(minutesBetween(a.join, a.leave));
      const on = [a.device, a.clientLabel].filter(Boolean);
      const where = on.length ? `on ${on[0]}${on[1] ? ` (${on[1]})` : ''}` : '';
      return m.type === 'call' ? (where || 'device not recorded') : `attended${d ? ` ${d}` : ''}${where ? ` ${where}` : ''}`;
    }).join(' and ');
  }
  return m.hiddenRecordCount
    ? `${m.hiddenRecordCount} record${m.hiddenRecordCount === 1 ? '' : 's'} of this ${noun} hidden (dismissed or filtered)`
    : `no ${m.type === 'call' ? 'participation' : 'attendance'} by this account recorded`;
}

/**
 * The description of a meeting/call row. Shared by BOTH generateDescription copies (through
 * describeRolledRow) so the screen and the reports say the same thing. No clock times: the
 * row's own timestamp column carries the time in the analyst's chosen zone. A call's direction
 * is deliberately not stated: the record marks an organizer, not who dialled.
 */
export function describeTeamsMeeting(event) {
  const m = event?._meeting;
  if (!m) return null;
  const upn = event.UserPrincipalName || 'This account';
  if (m.type === 'call') {
    const who = withWhom(m);
    const outcome = String(m.outcome || '').toLowerCase();
    const dur = m.durationMin ? `, ${durationLabel(m.durationMin)}` : '';
    const head = outcome === 'missed'
      ? `${upn} had a missed Teams call${who ? ` with ${who}` : ''}`
      : outcome === 'voicemail'
        ? `${upn} had a Teams call${who ? ` with ${who}` : ''} that went to voicemail`
        : `${upn} was on a Teams call${who ? ` with ${who}` : ''}${outcome === 'complete' ? ` (completed${dur})` : (dur ? ` (${dur.slice(2)})` : '')}`;
    return `${head}; ${ownPart(m, 'call')}`;
  }
  const dur = m.durationMin != null ? ` (${durationLabel(m.durationMin)})` : '';
  const head = m.organizerIsSubject
    ? `${upn} organized a Teams meeting${dur}`
    : `Teams meeting${dur} organized by another account`;
  const others = m.otherAttendeeCount
    ? `${m.otherAttendeeCount} other attendee${m.otherAttendeeCount === 1 ? '' : 's'}`
    : 'no other attendees recorded';
  return `${head}; ${ownPart(m, 'meeting')}; ${others}`;
}

// ── Evidence for the analyst ──────────────────────────────────────────────

function fingerprint(a) {
  return `${String(a.device || '').toLowerCase()}|${a.clientCode}|${a.localIp}`;
}
function deviceKey(a) {
  return `${String(a.device || '').toLowerCase()}|${a.clientCode}`;
}

/**
 * The case user's participation before the attacker's first recorded activity, keyed by
 * (device, Teams app, address) and by (device, Teams app). Built from the UNFILTERED general
 * events so a filter pill cannot shrink the baseline.
 *
 * @param {Array<object>} generalEvents   results.GeneralEvents (time correction applied or not)
 * @param {string|null}   windowStartIso  attackerWindow.firstAttackerActivity
 */
export function buildMeetingDeviceBaseline(generalEvents, windowStartIso) {
  const startMs = windowStartIso ? Date.parse(windowStartIso) : NaN;
  const available = !Number.isNaN(startMs);
  const full = new Map();
  const device = new Map();
  if (available) {
    for (const e of generalEvents || []) {
      const a = attendanceOf(e);
      if (!a || !a.isSubject || !a.join) continue;
      if (Date.parse(a.join) >= startMs) continue;
      const k = teamsGroupKey(e);
      for (const [map, key] of [[full, fingerprint(a)], [device, deviceKey(a)]]) {
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(k);
      }
    }
  }
  return { available, windowStart: available ? new Date(startMs).toISOString() : null, full, device };
}

/**
 * How one of the case user's participations compares with their participation before the
 * attacker arrived. States:
 *   'no_window'     -- no attacker window, so there is nothing to compare against
 *   'before_window' -- it began before the attacker's first recorded activity
 *   'known'         -- same device, Teams app and address as an earlier meeting or call
 *   'partial'       -- same device and Teams app, different address
 *   'new'           -- this device and app were not seen before the attacker arrived
 * `earlierMeetings` counts OTHER meetings/calls, never this one.
 */
export function assessAttendance(att, baseline, meetingId) {
  if (!att) return null;
  if (!baseline?.available) return { state: 'no_window', earlierMeetings: 0 };
  const others = (set) => (set ? set.size - (set.has(meetingId) ? 1 : 0) : 0);
  const fullN = others(baseline.full.get(fingerprint(att)));
  const devN = others(baseline.device.get(deviceKey(att)));
  if (att.join && Date.parse(att.join) < Date.parse(baseline.windowStart)) {
    return { state: 'before_window', earlierMeetings: fullN };
  }
  if (fullN > 0) return { state: 'known', earlierMeetings: fullN };
  if (devN > 0) return { state: 'partial', earlierMeetings: devN };
  return { state: 'new', earlierMeetings: 0 };
}

/** True when every one of the case user's participations was seen before the attacker arrived. */
export function meetingSeenBeforeAttacker(row, baseline) {
  const m = row?._meeting;
  if (!m || !m.subjectAttendances.length || !baseline?.available) return false;
  return m.subjectAttendances.every((a) => {
    const s = assessAttendance(a, baseline, m.meetingId)?.state;
    return s === 'known' || s === 'before_window';
  });
}

/**
 * The case user's own sign-ins (interactive and non-interactive) within ±N minutes of a join.
 * A non-interactive row is a (session, IP, hour) bucket, so it counts when its span overlaps
 * the window, not only when its first time falls inside it.
 */
export function nearbySignIns(att, { signIns = [], nonInteractive = [] } = {}, minutes = MEETING_SIGNIN_WINDOW_MIN) {
  const j = Date.parse(att?.join || '');
  if (Number.isNaN(j)) return [];
  const lo = j - minutes * 60000; const hi = j + minutes * 60000;
  const out = [];
  const push = (e, feed, t0, t1) => {
    if (Number.isNaN(t0)) return;
    const last = Number.isNaN(t1) ? t0 : t1;
    if (last < lo || t0 > hi) return;
    const nearest = t0 > j ? t0 : (last < j ? last : j);
    const apps = e.ApplicationName ? [e.ApplicationName]
      : (Array.isArray(e.Apps) ? e.Apps : String(e.Apps || '').split(',')).map((x) => String(x).trim()).filter(Boolean);
    out.push({
      eventId: e.EventId,
      time: e.EventTimeUTC,
      // A non-interactive row is a bucket: show its whole span, or a bucket that began an hour
      // earlier reads as out of place under "within N minutes".
      lastTime: feed === 'non-interactive' && e.LastEventTimeUTC && e.LastEventTimeUTC !== e.EventTimeUTC ? e.LastEventTimeUTC : '',
      feed,
      ip: e.IPAddress || '',
      device: e.DeviceHostname || e.DeviceName || '',
      app: apps.join(', '),
      apps,
      result: e.ResultStatus || '',
      deltaSec: Math.round((nearest - j) / 1000),
    });
  };
  for (const e of signIns) push(e, 'interactive', Date.parse(e.EventTimeUTC || ''), NaN);
  for (const e of nonInteractive) push(e, 'non-interactive', Date.parse(e.EventTimeUTC || ''), Date.parse(e.LastEventTimeUTC || ''));
  return out.sort((a, b) => Math.abs(a.deltaSec) - Math.abs(b.deltaSec)).slice(0, 6);
}
